import { lngLatToMercatorMeters, VISUAL_REVEAL_RADIUS_METERS } from '@repo/map-preferences/fog-tiles';
import type { PreferencesClientMessage } from '@repo/map-preferences/types';
import { addFogCircles, selectFogOfWarActive } from '@repo/store/maps/slices/mapData';
import type { AppDispatch, RootState } from '@repo/store/maps/store';
import type * as MapLibre from 'maplibre-gl';
import type { MutableRefObject } from 'react';
import { fogSpatialIndex } from './fog-spatial-index';

const MIN_SAMPLE_DISTANCE_M = 0.05;
const PATH_INTERP_STEP_M = 2;
const MAX_INTERP_STEPS = 500;

// Drop a new circle if its center is within 80% of VISUAL_REVEAL_RADIUS of an existing center — near-total overlap.
const DEDUP_RADIUS_SQ = (VISUAL_REVEAL_RADIUS_METERS * 0.8) ** 2;

export interface ExecuteFogRevealContext {
    dispatch: AppDispatch;
    getState: () => RootState;
    lastSampledRef: MutableRefObject<MapLibre.LngLat | null>;
    sendMessageRef?: MutableRefObject<((message: PreferencesClientMessage) => void) | null>;
    trustLivePosition?: boolean;
}

export function executeFogRevealAtLatLng(latLng: MapLibre.LngLat, ctx: ExecuteFogRevealContext): void {
    const state = ctx.getState();
    if (!state.mapData.mapData) return;
    if (!selectFogOfWarActive(state)) return;
    if (!ctx.trustLivePosition && !state.mapData.locationEnabled) return;

    const last = ctx.lastSampledRef.current;
    const distFromLast = last ? last.distanceTo(latLng) : Number.POSITIVE_INFINITY;
    if (last && distFromLast < MIN_SAMPLE_DISTANCE_M && !ctx.trustLivePosition) return;

    const positions: Array<[number, number]> = [];
    const shouldInterp = last && Number.isFinite(distFromLast);
    const stepCount = shouldInterp ? Math.ceil(distFromLast / PATH_INTERP_STEP_M) : 0;
    if (shouldInterp && stepCount > 0 && stepCount <= MAX_INTERP_STEPS && last) {
        for (let i = 1; i <= stepCount; i++) {
            const t = i / stepCount;
            positions.push(
                lngLatToMercatorMeters(last.lng + (latLng.lng - last.lng) * t, last.lat + (latLng.lat - last.lat) * t),
            );
        }
    } else {
        positions.push(lngLatToMercatorMeters(latLng.lng, latLng.lat));
    }

    const novel = fogSpatialIndex.getNovelCircles(positions, DEDUP_RADIUS_SQ);

    if (novel.length === 0) {
        ctx.lastSampledRef.current = latLng;
        return;
    }

    // Update the spatial index before dispatching so any re-render scheduled by the dispatch
    // observes a consistent state+index pair — otherwise the memo in useMapVisibleLocations
    // can filter pins against a stale index and cache an empty result.
    fogSpatialIndex.addCircles(novel);
    ctx.dispatch(addFogCircles(novel));

    if (ctx.sendMessageRef?.current) {
        ctx.sendMessageRef.current({ type: 'fog_circles_add', circles: novel });
    }

    ctx.lastSampledRef.current = latLng;
}
